import React from "react";
import { styled } from "@mui/material/styles";
import Button from "@mui/material/Button";
import GlossaryIcon from "@mui/icons-material/AutoStoriesOutlined";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

import {
  DOMAIN_NAME,
  PALETTE,
  PLAYER_STATUS,
  PLAYTIME_PHASE,
  getColorWithAlpha,
} from "../constants";
import Timer from "./Timer";

const { LOGIN, EXPLORE, KEEPGOING } = PLAYTIME_PHASE;
const DEFAULT_PLAYTIME_BG_COLOR = getColorWithAlpha(PALETTE.black, 0.25);
const PLAYTIME_PHASE_BG_COLORS = {
  [KEEPGOING]: getColorWithAlpha(PALETTE.black, 0.8),
};

const { PLAYING, PAUSED } = PLAYER_STATUS;

/** @todo enable displaying login instructions as a list */
const LOGIN_INSTRUCTION = `Teacher FAQs
- Students may start playing when finished customizing.
- You may end the timer early when all students are done.`;
const PLAYTIME_INSTRUCTIONS = {
  [LOGIN]: {
    [PLAYING]: LOGIN_INSTRUCTION,
    [PAUSED]: LOGIN_INSTRUCTION,
  },
  [EXPLORE]: {
    [PLAYING]: [
      "Click on the book icon (\u202f",
      GlossaryIcon,
      "\u202f) below to check block definitions and object names.",
    ],
    [PAUSED]: "Play this puzzle to prepare for the upcoming discussion.",
    [`${PAUSED}-1`]:
      "Try playing the first couple of puzzles to get a feel for the game.",
  },
  [KEEPGOING]: {
    [PLAYING]:
      "TIP:\nPress 'Z' as a shortcut to undo\n\nPress 'R' as a shortcut to reset",
    [PAUSED]:
      "Now's a good chance to walk around and see how your students are doing!",
  },
};

/** @todo clean up this temporary blahness that only works on QA */
const GAME_URL = process.env.REACT_APP_USE_ALT_DOMAIN
  ? 'https://play-alt-rewriters-qa.web.app/'
  : `https://play.${DOMAIN_NAME}/`;

const Gameplay = ({
  gameRef,
  playtimePhase,
  gameplayRoute,
  isGamePlaying,
  setIsGamePlaying,
  timerTimes,
  setDisableArrowControls,
  handleNavigation,
  nextSlideLabel,
  timerModalInfo,
  setTimerModalInfo,
  startTimerModalCallback,
  isShowingGlossary,
  isTutorialTooltipBlockingInputs,
  completeActiveTutorial,
  customizationNumCompleted,
  lessonNum,
}) => {
  const handlePlayBtnClick = () => {
    if (gameRef.current) {
      setIsGamePlaying(true);
      if (!isTutorialTooltipBlockingInputs) {
        gameRef.current.contentWindow.focus();
      }
    }
  };

  return (
    <div id="gameplay">
      <div id="gameplay-container">
        <iframe
          ref={gameRef}
          id="game"
          key={playtimePhase}
          title="Game Play"
          scrolling="no"
          tabIndex={-1}
          onLoad={() => {
            /**
             * checking isGamePlaying is necessary since user can press play
             * button before game iframe loads in which case it should have
             * focus over one-click
             */
            if (!isGamePlaying) {
              window.focus();
            }
          }}
          src={`${GAME_URL}${gameplayRoute}`}
          style={{
            pointerEvents:
              !isGamePlaying || timerModalInfo.isVisible || isShowingGlossary
                ? "none"
                : "auto",
          }}
        />
        {!isGamePlaying && (
          <GamePaused
            handlePlayBtnClick={handlePlayBtnClick}
            playtimePhase={playtimePhase}
          />
        )}
        <div className="gameplay-sidebar">
          {playtimePhase === LOGIN && (
            <StudentsDoneCustomizing
              customizationNumCompleted={customizationNumCompleted}
            />
          )}
          <Timer
            type="game"
            key={playtimePhase}
            phase={playtimePhase}
            timerTimes={timerTimes}
            setDisableArrowControls={setDisableArrowControls}
            handleNavigation={handleNavigation}
            nextSlideLabel={nextSlideLabel}
            timerModalInfo={timerModalInfo}
            setTimerModalInfo={setTimerModalInfo}
            isGamePlaying={isGamePlaying}
            setIsGamePlaying={setIsGamePlaying}
            startTimerModalCallback={startTimerModalCallback}
            isTutorialTooltipBlockingInputs={isTutorialTooltipBlockingInputs}
            completeActiveTutorial={completeActiveTutorial}
          />
          <Instructions
            playtimePhase={playtimePhase}
            isGamePlaying={isGamePlaying}
            lessonNum={lessonNum}
          />
        </div>
      </div>
    </div>
  );
};

export default Gameplay;

const GamePaused = ({ handlePlayBtnClick, playtimePhase }) => {
  const StyledButton = styled(Button)(() => ({
    alignSelf: "center",
    width: "350px",
    height: "64px",
    fontFamily: "Roboto Mono",
    fontWeight: "bold",
    fontSize: "24px",
    textTransform: "capitalize",
    color: PALETTE.green,
    backgroundColor: getColorWithAlpha(PALETTE.black, 0.7),
    "&:hover": {
      backgroundColor: PALETTE.green,
      color: PALETTE.black,
    },
  }));
  return (
    <div
      id="game-paused"
      style={{
        backgroundColor:
          PLAYTIME_PHASE_BG_COLORS[playtimePhase] ?? DEFAULT_PLAYTIME_BG_COLOR,
      }}
    >
      <StyledButton
        onClick={handlePlayBtnClick}
        startIcon={<PlayArrowIcon style={{ width: "30px", height: "30px" }} />}
      >
        Start Teacher Play
      </StyledButton>
    </div>
  );
};

const Instructions = ({ isGamePlaying, lessonNum, playtimePhase }) => {
  const baseStudentStateKey = isGamePlaying ? PLAYING : PAUSED;
  const lessonStudentStateKey = `${baseStudentStateKey}-${lessonNum}`;

  const instructions =
    PLAYTIME_INSTRUCTIONS[playtimePhase]?.[lessonStudentStateKey] ??
    PLAYTIME_INSTRUCTIONS[playtimePhase]?.[baseStudentStateKey];

  const instructionsArray = Array.isArray(instructions)
    ? instructions
    : [instructions];

  return (
    <div id="game-instructions">
      <span>
        {instructionsArray.map((textOrIcon) => {
          if (typeof textOrIcon === "string") {
            return textOrIcon;
          } else {
            const IconComponent = textOrIcon;
            return <IconComponent />;
          }
        })}
      </span>
    </div>
  );
};

const StudentsDoneCustomizing = ({ customizationNumCompleted = 0 }) => {
  return (
    <div className="students-done-customizing">
      <div className="count">{customizationNumCompleted}</div>
      <div className="text">Students Done</div>
    </div>
  );
};
